import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import axiosInstance from "../../common/axiosInstance";
import { useNavigate } from "react-router-dom";
import { getUserTrainingAnalytics, getUserDirectory } from "../../service/api";
import {
  Users,
  Calendar,
  BookOpen,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  RefreshCw,
} from "lucide-react";
import { useTrainingModule } from "./TrainingModuleContext";

interface UserTrainingData {
  id: string;
  userId: string;
  tenantId: string;
  traineeStats: {
    totalTrainings: number;
    completedTrainings: number;
    attendedTrainings: number;
    averagePreTestScore: number;
    averagePostTestScore: number;
    preTestScoreCount: number;
    postTestScoreCount: number;
    improvementRate: number | null;
    lastTrainingDate: string | null;
    certifications: string[];
    trainingHistory: Array<{
      trainingId: string;
      topic: string;
      category: string;
      date: string;
      attendance: {
        "Pre-Test": "Present" | "Absent";
        "Post-Test": "Present" | "Absent";
      };
      preTestScore: number | null;
      postTestScore: number | null;
      status: string;
    }>;
    // Additional fields from backend
    preTestsTaken?: number;
    totalTestsTaken?: number;
    postTestsTaken?: number;
  };
  trainerStats: {
    totalTrainingsConducted: number;
    activeTrainings: number;
    completedTrainings: number;
    lastTrainingConducted: string | null;
    trainingHistory: Array<{
      trainingId: string;
      topic: string;
      category: string;
      date: string;
      traineeCount: number;
      status: string;
    }>;
  };
  trainingRecords: Array<{
    trainingId: string;
    role: "trainee" | "trainer";
    topic: string;
    category: string;
    date: string;
    attendance: "Present" | "Absent";
    preTestScore: number | null;
    postTestScore: number | null;
    improvement: number | null;
    status: "Active" | "Completed" | "Cancelled";
  }>;
  performanceAnalytics: {
    categoryPerformance: Record<
      string,
      {
        totalTrainings: number;
        completedTrainings: number;
        averagePreTestScore: number;
        averagePostTestScore: number;
      }
    >;
    monthlyProgress: Array<{
      month: string;
      trainings: number;
      averageScore: number;
    }>;
    skillGaps: string[];
    recommendations: string[];
  };
  certifications: Array<{
    name: string;
    date: string;
    expiryDate?: string;
    status: "Active" | "Expired" | "Pending";
  }>;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
}

const UserTrainingAnalytics: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const [userTrainingData, setUserTrainingData] =
    useState<UserTrainingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [activeTab, setActiveTab] = useState("overview");
  const [users, setUsers] = useState<
    Array<{
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      premise?: string | Array<{ $oid: string }> | string[];
      premisesName?: string;
    }>
  >([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [premises, setPremises] = useState<
    Array<{ _id: string; premisesName: string }>
  >([]);
  const [selectedPremisesId, setSelectedPremisesId] = useState<string>("");
  const [loadingPremises, setLoadingPremises] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Check if user is org admin
  const userRoles = user?.roles || [];
  const isOrgAdmin = userRoles.includes("orgadmin");

  const matchesModule = (record: {
    moduleType?: string;
    category?: string;
  }) =>
    record?.moduleType === moduleType ||
    (isFixedCategory && record?.category === validCategories[0]);

  const fetchPremises = async () => {
    try {
      setLoadingPremises(true);
      const response = await axiosInstance.get("/api/premises");
      const premisesData = response.data?.data || response.data || [];
      const processedPremises = Array.isArray(premisesData) ? premisesData : [];
      setPremises(processedPremises);
    } catch (err: any) {
      console.error("Failed to fetch premises:", err);
      setPremises([]);
    } finally {
      setLoadingPremises(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getUserDirectory();

      // Handle the response format based on the API structure
      const userData =
        response.data?.data || response.data?.message || response.data || [];

      const processedUsers = Array.isArray(userData) ? userData : [];

      // Map premises names to users if available
      const usersWithPremises = processedUsers.map((user) => {
        let userPremisesName = "";

        // Handle different premise data types
        if (user.premise) {
          if (typeof user.premise === "string") {
            const userPremises = premises.find((p) => p._id === user.premise);
            userPremisesName = userPremises?.premisesName || "";
          } else if (Array.isArray(user.premise)) {
            // Handle array of premises
            const premiseIds = user.premise.map((p) =>
              typeof p === "string" ? p : p.$oid,
            );
            const userPremises = premises.find((p) =>
              premiseIds.includes(p._id),
            );
            userPremisesName = userPremises?.premisesName || "";
          }
        }

        return {
          ...user,
          premisesName: userPremisesName,
        };
      });

      setUsers(usersWithPremises);
    } catch (err: any) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserTrainingAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      // Test if backend is reachable
      try {
        const testResponse = await axiosInstance.get("/api/orgUser/directory");
      } catch (testErr: any) {
        console.error(
          "Backend connectivity test failed:",
          testErr.response?.status,
        );
      }

      const response = await getUserTrainingAnalytics(selectedUserId);
      // Handle the response structure and provide default values for missing fields
      const responseData = response.data || response;

      // Calculate derived statistics from the actual API response
      const traineeHistory = responseData.traineeStats?.trainingHistory || [];
      const trainerHistory = responseData.trainerStats?.trainingHistory || [];
      const filteredTraineeHistory = traineeHistory.filter(matchesModule);
      const filteredTrainerHistory = trainerHistory.filter(matchesModule);

      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);

      // Trainee perspective:
      // A training in history is:
      // - "Completed" if trainee finished Post-Test or training date is past with pre/post tests done
      // - "Inactive" if training date is past and trainee didn't take tests, or training marked inactive
      // - "Active" if training date is today/future
      const processedTraineeHistory = filteredTraineeHistory.map((t: any) => {
        const tDate = t.date ? new Date(t.date) : new Date();
        const isPast = tDate < todayMidnight;
        const hasCompletedPostTest =
          t.postTestScore !== null && t.postTestScore !== undefined;
        const hasPreTest =
          t.preTestScore !== null && t.preTestScore !== undefined;

        let status = t.status || "Active";
        if (t.status === "Cancelled") {
          status = "Cancelled";
        } else if (hasCompletedPostTest) {
          status = "Completed";
        } else if (isPast) {
          status = hasPreTest ? "Completed" : "Inactive";
        } else if (t.status === "Inactive") {
          status = "Inactive";
        } else {
          status = "Active";
        }

        return {
          ...t,
          status,
        };
      });

      const totalTrainings = processedTraineeHistory.length;
      const completedTrainings = processedTraineeHistory.filter(
        (t: any) => t.status === "Completed",
      ).length;
      const attendedTrainings = processedTraineeHistory.filter(
        (t: any) =>
          (t.attendance &&
            (t.attendance["Pre-Test"] === "Present" ||
              t.attendance["Post-Test"] === "Present")) ||
          t.preTestScore !== null ||
          t.postTestScore !== null,
      ).length;

      const preTestScores = processedTraineeHistory
        .filter(
          (t: any) => t.preTestScore !== null && t.preTestScore !== undefined,
        )
        .map((t: any) => t.preTestScore);
      const postTestScores = processedTraineeHistory
        .filter(
          (t: any) =>
            t.postTestScore !== null && t.postTestScore !== undefined,
        )
        .map((t: any) => t.postTestScore);

      const preTestsTaken = preTestScores.length;
      const postTestsTaken = postTestScores.length;
      const totalTestsTaken = preTestsTaken + postTestsTaken;

      const averagePreTestScore =
        preTestScores.length > 0
          ? preTestScores.reduce((sum: number, score: number) => sum + score, 0) /
          preTestScores.length
          : 0;
      const preTestScoreCount = preTestScores.length;

      const averagePostTestScore =
        postTestScores.length > 0
          ? postTestScores.reduce(
            (sum: number, score: number) => sum + score,
            0,
          ) / postTestScores.length
          : 0;
      const postTestScoreCount = postTestScores.length;

      const improvementRate =
        averagePreTestScore > 0 && postTestScores.length > 0
          ? ((averagePostTestScore - averagePreTestScore) /
            averagePreTestScore) *
          100
          : null;

      // Get last training date
      const lastTrainingDate =
        processedTraineeHistory.length > 0
          ? processedTraineeHistory[processedTraineeHistory.length - 1].date
          : null;

      // Trainer perspective:
      // A training conducted by trainer is:
      // - "Completed" if training date is in the past, or status is Completed/Inactive
      // - "Active" if training date is today/future and status is Active
      const processedTrainerHistory = filteredTrainerHistory.map((t: any) => {
        const tDate = t.date ? new Date(t.date) : new Date();
        const isPast = tDate < todayMidnight;

        let status = t.status || "Active";
        if (t.status === "Cancelled") {
          status = "Cancelled";
        } else if (t.status === "Completed" || t.status === "Inactive" || isPast) {
          status = t.status === "Inactive" ? "Inactive" : "Completed";
        } else {
          status = "Active";
        }

        return {
          ...t,
          status,
        };
      });

      const totalTrainingsConducted = processedTrainerHistory.length;
      const completedTrainingsConducted = processedTrainerHistory.filter(
        (t: any) => t.status === "Completed" || t.status === "Inactive",
      ).length;
      const activeTrainingsConducted = processedTrainerHistory.filter(
        (t: any) => t.status === "Active",
      ).length;
      const lastTrainingConducted =
        processedTrainerHistory.length > 0
          ? processedTrainerHistory[processedTrainerHistory.length - 1].date
          : null;

      const categoryPerformanceData =
        responseData.performanceAnalytics?.categoryPerformance || {};
      const filteredCategoryPerformance: Record<
        string,
        {
          totalTrainings: number;
          completedTrainings: number;
          averagePreTestScore: number;
          averagePostTestScore: number;
        }
      > = {};
      validCategories.forEach((cat) => {
        const value = categoryPerformanceData[cat];
        if (value && typeof value === "object") {
          filteredCategoryPerformance[cat] = {
            totalTrainings: Number(value.totalTrainings) || 0,
            completedTrainings: Number(value.completedTrainings) || 0,
            averagePreTestScore: Number(value.averagePreTestScore) || 0,
            averagePostTestScore: Number(value.averagePostTestScore) || 0,
          };
        }
      });

      const filteredMonthlyProgress = (
        responseData.performanceAnalytics?.monthlyProgress || []
      ).filter(matchesModule);
      const sanitizedMonthlyProgress = filteredMonthlyProgress.map(
        (entry: any) => ({
          month: entry?.month || "",
          trainings: Number(entry?.trainings) || 0,
          averageScore: Number(entry?.averageScore) || 0,
        }),
      );

      const filteredSkillGaps = (
        responseData.performanceAnalytics?.skillGaps || []
      ).filter(matchesModule);
      const sanitizedSkillGaps = filteredSkillGaps
        .map((gap: any) => (typeof gap === "string" ? gap : gap?.name || ""))
        .filter((gap: string): gap is string => gap.length > 0);

      const filteredRecommendations = (
        responseData.performanceAnalytics?.recommendations || []
      ).filter(matchesModule);
      const sanitizedRecommendations = filteredRecommendations
        .map((rec: any) => (typeof rec === "string" ? rec : rec?.text || ""))
        .filter((rec: string): rec is string => rec.length > 0);

      const sanitizedTrainingRecords = (responseData.trainingRecords || [])
        .filter(matchesModule)
        .map((record: any) => ({
          trainingId: record?.trainingId || record?._id || "",
          role: record?.role === "trainer" ? "trainer" : "trainee",
          topic: record?.topic || "",
          category: record?.category || "",
          date: record?.date || "",
          attendance: record?.attendance === "Absent" ? "Absent" : "Present",
          preTestScore:
            typeof record?.preTestScore === "number"
              ? record.preTestScore
              : null,
          postTestScore:
            typeof record?.postTestScore === "number"
              ? record.postTestScore
              : null,
          improvement:
            typeof record?.improvement === "number" ? record.improvement : null,
          status:
            record?.status === "Completed" ||
              record?.status === "Cancelled" ||
              record?.status === "Active"
              ? record.status
              : "Active",
        }));

      // Ensure all required fields exist with default values
      const processedData: UserTrainingData = {
        id: responseData.id || responseData._id,
        userId: responseData.userId,
        tenantId: responseData.tenantId,
        traineeStats: {
          totalTrainings: totalTrainings,
          completedTrainings: completedTrainings,
          attendedTrainings: attendedTrainings,
          averagePreTestScore: averagePreTestScore,
          averagePostTestScore: averagePostTestScore,
          preTestScoreCount: preTestScoreCount,
          postTestScoreCount: postTestScoreCount,
          improvementRate: improvementRate,
          lastTrainingDate: lastTrainingDate,
          certifications: responseData.traineeStats?.certifications || [],
          trainingHistory: processedTraineeHistory,
          preTestsTaken: preTestsTaken,
          totalTestsTaken: totalTestsTaken,
          postTestsTaken: postTestsTaken,
        },
        trainerStats: {
          totalTrainingsConducted: totalTrainingsConducted,
          activeTrainings: activeTrainingsConducted,
          completedTrainings: completedTrainingsConducted,
          lastTrainingConducted: lastTrainingConducted,
          trainingHistory: processedTrainerHistory,
        },
        trainingRecords: sanitizedTrainingRecords,
        performanceAnalytics: {
          categoryPerformance: filteredCategoryPerformance,
          monthlyProgress: sanitizedMonthlyProgress,
          skillGaps: sanitizedSkillGaps,
          recommendations: sanitizedRecommendations,
        },
        certifications: responseData.certifications || [],
        lastUpdated: responseData.lastUpdated || responseData.updatedAt,
        createdAt: responseData.createdAt,
        updatedAt: responseData.updatedAt,
      };
      setUserTrainingData(processedData);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching training analytics:", err);
      console.error("Error details:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        url: err.config?.url,
        method: err.config?.method,
        headers: err.config?.headers,
      });

      let errorMessage = "Failed to fetch training analytics";

      if (err.response?.status === 401) {
        errorMessage =
          "Authentication failed. Please check if the backend endpoint exists and you have proper permissions.";
      } else if (err.response?.status === 404) {
        errorMessage =
          "User training analytics endpoint not found. Backend endpoint may not be implemented.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      // If it's an authentication error, redirect to login
      if (err.response?.status === 401) {
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (selectedUserId) {
      setRefreshing(true);
      await fetchUserTrainingAnalytics();
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isOrgAdmin) {
      navigate(paths.dashboard);
      return;
    }

    fetchPremises();
  }, [isOrgAdmin, navigate]);

  // Refetch users when premises data is loaded to map premises names
  useEffect(() => {
    if (premises.length > 0 && isOrgAdmin) {
      fetchUsers();
    }
  }, [premises, isOrgAdmin]);

  useEffect(() => {
    if (selectedUserId && selectedUserId.trim() !== "") {
      fetchUserTrainingAnalytics();
    }
  }, [selectedUserId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getScoreBadge = (score: number | null | undefined, isUpcoming: boolean) => {
    const label =
      score !== null && score !== undefined
        ? `${Math.round(score)}%`
        : isUpcoming
          ? "Upcoming"
          : "Not Taken";
    const colorClass =
      score !== null && score !== undefined
        ? "bg-blue-50 text-blue-800"
        : "bg-gray-100 text-gray-600";
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
      >
        {label}
      </span>
    );
  };

  const getPerformanceBadge = (
    preTestScore: number | null | undefined,
    postTestScore: number | null | undefined,
    isUpcoming: boolean,
  ) => {
    if (
      preTestScore === null ||
      preTestScore === undefined ||
      postTestScore === null ||
      postTestScore === undefined
    ) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          {isUpcoming ? "Upcoming" : "N/A"}
        </span>
      );
    }

    const diff = Math.round(postTestScore - preTestScore);
    if (diff === 0) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          No Change
        </span>
      );
    }

    const improved = diff > 0;
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${improved ? "bg-green-100 text-green-800" : "bg-red-50 text-red-600"
          }`}
      >
        Performance {improved ? "Improved" : "Dropped"} By {Math.abs(diff)}%
      </span>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800";
      case "Active":
        return "bg-blue-100 text-blue-800";
      case "Cancelled":
        return "bg-red-100 text-red-800";
      case "Inactive":
        return "bg-red-50 text-red-600";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Filter users based on selected premises
  const filteredUsers = selectedPremisesId
    ? users.filter((user) => {
      if (!user.premise) return false;

      if (typeof user.premise === "string") {
        return user.premise === selectedPremisesId;
      } else if (Array.isArray(user.premise)) {
        const premiseIds = user.premise.map((p) =>
          typeof p === "string" ? p : p.$oid,
        );
        return premiseIds.includes(selectedPremisesId);
      }

      return false;
    })
    : users;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  // If not org admin, redirect
  if (!isOrgAdmin) {
    return null; // Will redirect in useEffect
  }

  // If no user is selected, show a message to select from dropdown
  if (!selectedUserId) {
    return (
      <div className="py-4 px-2 max-w-10xl mx-auto mt-8 min-h-[calc(100vh-180px)]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between px-2 gap-4">
          <h1 className="text-3xl font-bold text-gray-900">
            User Training Analysis
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Premises Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">
                Premises:
              </label>
              <select
                value={selectedPremisesId}
                onChange={(e) => {
                  setSelectedPremisesId(e.target.value);
                  setSelectedUserId(""); // Reset user selection when premises changes
                }}
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px] z-50 relative"
                style={{ zIndex: 1000 }}
              >
                <option value="">All Premises</option>
                {premises.map((premise) => (
                  <option key={premise._id} value={premise._id}>
                    {premise.premisesName}
                  </option>
                ))}
              </select>
            </div>

            {/* User Selection Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">
                View Analysis for:
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loadingUsers}
              >
                <option value="">Select a user...</option>
                {filteredUsers.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span>Back</span>
            </button>
          </div>
        </div>

        <div className="bg-white p-8 rounded-lg border shadow-sm mt-6">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Select a User
            </h2>
            <p className="text-gray-600">
              Please select a user from the dropdown above to view their
              training analytics
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!userTrainingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">
          No training data available for selected user
        </p>
      </div>
    );
  }

  const {
    traineeStats = {
      totalTrainings: 0,
      completedTrainings: 0,
      attendedTrainings: 0,
      averagePreTestScore: 0,
      averagePostTestScore: 0,
      preTestScoreCount: 0,
      postTestScoreCount: 0,
      improvementRate: 0,
      lastTrainingDate: null,
      certifications: [],
      trainingHistory: [],
    },
    trainerStats = {
      totalTrainingsConducted: 0,
      activeTrainings: 0,
      completedTrainings: 0,
      lastTrainingConducted: null,
      trainingHistory: [],
    },
    trainingRecords = [],
    performanceAnalytics = {
      categoryPerformance: {},
      monthlyProgress: [],
      skillGaps: [],
      recommendations: [],
    },
    certifications = [],
  } = userTrainingData || {};

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Trainings
              </p>
              <p className="text-2xl font-bold">
                {(traineeStats.totalTrainings || 0) +
                  (trainerStats.totalTrainingsConducted || 0)}
              </p>
              <p className="text-xs text-gray-500">
                {traineeStats.totalTrainings || 0} as trainee,{" "}
                {trainerStats.totalTrainingsConducted || 0} as trainer
              </p>
            </div>
            <BookOpen className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Completion Rate (as Trainee)
              </p>

              {(() => {
                const completedCount = traineeStats.completedTrainings;
                const totalCount = traineeStats.totalTrainings;

                const completionRate =
                  totalCount > 0
                    ? Math.round((completedCount / totalCount) * 100)
                    : 0;

                return (
                  <>
                    <p className="text-2xl font-bold">{completionRate}%</p>

                    <p className="text-xs text-gray-500">
                      {completedCount} of {totalCount} completed
                    </p>
                  </>
                );
              })()}
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Training Conduction Rate (as Trainer)
              </p>

              {(() => {
                const completedCount = trainerStats.completedTrainings;
                const totalCount = trainerStats.totalTrainingsConducted;

                const conductionRate =
                  totalCount > 0
                    ? Math.round((completedCount / totalCount) * 100)
                    : 0;

                return (
                  <>
                    <p className="text-2xl font-bold">{conductionRate}%</p>

                    <p className="text-xs text-gray-500">
                      {completedCount} of {totalCount} completed
                    </p>
                  </>
                );
              })()}
            </div>
            <Users className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Role Distribution */}
      <div className="bg-white p-6 rounded-lg border shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Role Distribution</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span>Trainee Sessions</span>
            <span className="font-semibold">
              {traineeStats.trainingHistory.length}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span>Trainer Sessions</span>
            <span className="font-semibold">
              {trainerStats.trainingHistory.length}
            </span>
          </div>
        </div>
      </div>

      {/* Category Performance (as Trainee) */}
      {/* {Object.keys(performanceAnalytics.categoryPerformance).length > 0 && (
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4">
            Category Performance (as Trainee)
          </h3>
          <div className="space-y-3">
            {Object.entries(performanceAnalytics.categoryPerformance).map(
              ([category, stats]) => (
                <div
                  key={category}
                  className="flex items-center justify-between border-b last:border-b-0 pb-3 last:pb-0"
                >
                  <div>
                    <p className="font-medium">{category}</p>
                    <p className="text-xs text-gray-500">
                      {stats.completedTrainings} of {stats.totalTrainings}{" "}
                      completed
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>Pre: {Math.round(stats.averagePreTestScore)}%</p>
                    <p>Post: {Math.round(stats.averagePostTestScore)}%</p>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )} */}
    </div>
  );

  const renderTraineeStats = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Training Summary
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Total Trainings:</span>
              <span className="font-semibold">
                {traineeStats.totalTrainings}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Completed:</span>
              <span className="font-semibold text-green-600">
                {traineeStats.completedTrainings}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Attended:</span>
              <span className="font-semibold text-blue-600">
                {traineeStats.attendedTrainings}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Recent Activity
          </h3>
          <p className="text-sm text-gray-600">
            Last Training:{" "}
            {traineeStats.lastTrainingDate
              ? formatDate(traineeStats.lastTrainingDate)
              : "N/A"}
          </p>
        </div>
      </div>

      {/* Training History */}
      <div className="bg-white p-6 rounded-lg border shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Training History</h3>
        <div className="space-y-4">
          {traineeStats.trainingHistory.length > 0 ? (
            traineeStats.trainingHistory.map((training, index) => {
              const isUpcoming = training.status === "Active";

              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <h4 className="font-semibold">{training.topic}</h4>
                    <p className="text-sm text-gray-600">
                      {formatDate(training.date)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Category: {training.category}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        Pre-Test: {getScoreBadge(training.preTestScore, isUpcoming)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        Post-Test: {getScoreBadge(training.postTestScore, isUpcoming)}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        Result:{" "}
                        {getPerformanceBadge(
                          training.preTestScore,
                          training.postTestScore,
                          isUpcoming,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No training history available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTrainerStats = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Training Conducted
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Total Conducted:</span>
              <span className="font-semibold">
                {trainerStats.totalTrainingsConducted}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Active:</span>
              <span className="font-semibold text-blue-600">
                {trainerStats.activeTrainings}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Completed:</span>
              <span className="font-semibold text-green-600">
                {trainerStats.completedTrainings}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Last Training
          </h3>
          <p className="text-sm text-gray-600">
            {trainerStats.lastTrainingConducted
              ? formatDate(trainerStats.lastTrainingConducted)
              : "N/A"}
          </p>
        </div>
      </div>

      {/* Trainer History */}
      <div className="bg-white p-6 rounded-lg border shadow-sm">
        <h3 className="text-lg font-semibold mb-4">
          Training Sessions Conducted
        </h3>
        <div className="space-y-4">
          {trainerStats.trainingHistory.length > 0 ? (
            trainerStats.trainingHistory.map((training, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex-1">
                  <h4 className="font-semibold">{training.topic}</h4>
                  <p className="text-sm text-gray-600">
                    {formatDate(training.date)}
                  </p>
                  <p className="text-xs text-gray-500">
                    Category: {training.category}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      training.status,
                    )}`}
                  >
                    {training.status}
                  </span>
                  <span className="text-sm text-gray-600">
                    {training.traineeCount} trainees
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No training sessions conducted</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="py-4 px-2 max-w-10xl mx-auto mt-8 min-h-[calc(100vh-180px)]">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">
          User Training Analysis
        </h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Premises Filter Dropdown */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">
              Premises:
            </label>
            <select
              value={selectedPremisesId}
              onChange={(e) => {
                setSelectedPremisesId(e.target.value);
                setSelectedUserId(""); // Reset user selection when premises changes
              }}
              className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px] z-50 relative"
              style={{ zIndex: 1000 }}
            >
              <option value="">All Premises</option>
              {premises.map((premise) => (
                <option key={premise._id} value={premise._id}>
                  {premise.premisesName}
                </option>
              ))}
            </select>
          </div>

          {/* User Selection Dropdown */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">
              View Analysis for:
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loadingUsers}
            >
              <option value="">Select a user...</option>
              {filteredUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <Activity className="h-6 w-6 text-blue-600" />
            <span className="text-sm text-gray-500">
              Last updated: {formatDate(userTrainingData.lastUpdated)}
            </span>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
          </button>

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            className={`px-4 py-2 rounded-lg transition-colors ${activeTab === "overview"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`px-4 py-2 rounded-lg transition-colors ${activeTab === "trainee"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setActiveTab("trainee")}
          >
            As Trainee
          </button>
          <button
            className={`px-4 py-2 rounded-lg transition-colors ${activeTab === "trainer"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            onClick={() => setActiveTab("trainer")}
          >
            As Trainer
          </button>
        </div>

        <div className="space-y-6">
          {activeTab === "overview" && renderOverview()}
          {activeTab === "trainee" && renderTraineeStats()}
          {activeTab === "trainer" && renderTrainerStats()}
        </div>
      </div>
    </div>
  );
};

export default UserTrainingAnalytics;
